import { useCallback, useEffect, useState } from 'react';
import { useToastContext } from '@/contexts/toastContext';
import useApiSubmit from '@/hooks/useApiSubmit';
import candidatesServices, {
  UploadCandidateProfileType,
  PartialCandidateToSubmit,
} from '@/services/candidates';
import CandidateProfilePictureManagerComponent from './CandidateProfilePictureManager.component';

export type CandidateProfilePictureManagerProps = {
  refetchCandidate?: () => void;
  firstName: string;
  lastName?: string;
  pictureUrl?: string;
  id: string;
  pictureIsEditable?: boolean;
  isFromHome?: boolean;
};

export default function CandidateProfilePictureManager({
  firstName,
  id,
  lastName,
  pictureUrl,
  pictureIsEditable,
  refetchCandidate,
  isFromHome,
}: CandidateProfilePictureManagerProps): JSX.Element {
  const [isUploadPictureOpen, setIsUploadPictureOpen] = useState(false);
  const [isEditingOptionOpen, setIsEditingOptionOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const toast = useToastContext();

  const updateCandidateMutation = useApiSubmit<any, PartialCandidateToSubmit>(
    candidatesServices.updateCandidateIndividuallyById
  );

  const uploadCandidateProfilePictureMutation = useApiSubmit<any, UploadCandidateProfileType>(
    candidatesServices.uploadCandidateProfilePicture
  );

  const uploadMyProfilePictureMutation = useApiSubmit<any, UploadCandidateProfileType>(
    candidatesServices.uploadMyProfilePicture
  );

  const deleteCandidateProfilePictureMutation = useApiSubmit<any, UploadCandidateProfileType>(
    candidatesServices.deleteCandidateProfilePicture
  );

  const deleteMyProfilePictureMutation = useApiSubmit<any, UploadCandidateProfileType>(
    candidatesServices.deleteMyProfilePicture
  );

  const uploadProfilePictureMutation = isFromHome
    ? uploadMyProfilePictureMutation
    : uploadCandidateProfilePictureMutation;
  const deleteProfilePictureMutation = isFromHome
    ? deleteMyProfilePictureMutation
    : deleteCandidateProfilePictureMutation;

  const onCloseUploadModal = (): void => {
    setIsUploadPictureOpen(false);
    setIsEditingOptionOpen(false);
  };

  const onOpenUploadModal = (): void => {
    setIsUploadPictureOpen(true);
  };

  const onCloseDeleteModal = (): void => {
    setIsDeleteModalOpen(false);
  };

  const onOpenDeleteModal = (): void => {
    setIsDeleteModalOpen(true);
    setIsEditingOptionOpen(false);
  };

  const onOpenEditingOptions = (): void => {
    setIsEditingOptionOpen(true);
  };

  const onSelectCandidateProfilePicture = async (image: any): Promise<void> => {
    try {
      const imageFile = new FormData();
      imageFile.append('imageFile', image?.imageFiles[0]?.file);
      imageFile.append('imageUrl', image?.imageUrl);
      await uploadProfilePictureMutation.mutateAsync({
        personId: id,
        imageFile,
      });
      if (refetchCandidate) {
        refetchCandidate();
      }
      onCloseUploadModal();
      setIsEditingOptionOpen(false);
      toast.showSuccessFeedback('The candidate picture has been uploaded.');
    } catch (err) {
      toast.showErrorFeedback('There was a problem uploading the candidate picture.');
    }
  };

  const onDeleteCandidatePicture = async (): Promise<void> => {
    try {
      await deleteProfilePictureMutation.mutateAsync({
        personId: id,
      });
      setIsDeleteModalOpen(false);
      setIsEditingOptionOpen(false);
      if (refetchCandidate) {
        refetchCandidate();
      }
      toast.showSuccessFeedback('The candidate picture has been deleted.');
    } catch (err) {
      toast.showErrorFeedback('There was a problem deleting the candidate picture.');
    }
  };

  const watchPageClick = useCallback((event) => {
    const eventPath = event.composedPath() ?? [];
    const componentIDs = ['candidate-profile-picture-manager'];
    const checkPathId = (path): boolean => componentIDs.includes(path.id);
    const hasClickedInsideComponent = eventPath.some(checkPathId);

    if (!hasClickedInsideComponent) {
      setIsEditingOptionOpen(false);
    }
  }, []);

  useEffect(() => {
    window.addEventListener('click', watchPageClick);
    return () => window.removeEventListener('click', watchPageClick);
  }, [watchPageClick]);

  return (
    <CandidateProfilePictureManagerComponent
      isDeleting={updateCandidateMutation.isLoading}
      pictureIsEditable={pictureIsEditable}
      isSaving={uploadProfilePictureMutation.isLoading}
      firstName={firstName}
      lastName={lastName ?? ''}
      pictureUrl={pictureUrl ?? ''}
      onCloseUploadModal={onCloseUploadModal}
      onOpenUploadModal={onOpenUploadModal}
      onSelectCandidateProfilePicture={onSelectCandidateProfilePicture}
      isEditingOptionOpen={isEditingOptionOpen}
      isDeleteModalOpen={isDeleteModalOpen}
      isUploadPictureOpen={isUploadPictureOpen}
      onDeleteCandidatePicture={onDeleteCandidatePicture}
      onCloseDeleteModal={onCloseDeleteModal}
      onOpenDeleteModal={onOpenDeleteModal}
      onOpenEditingOptions={onOpenEditingOptions}
    />
  );
}
